import { getOrderHistories } from '../../../api/orderHistoryApi'
import LinkButton from '../../../components/link-button/link-button'
import config from '../../../config'
import { getDateTime } from '../../../helpers/utils'
import translate from '../../../i18n'
import { PageInfo } from '../../../share-types'
import { formatOrderHistoryStatus, orderHistoryStatusOptions } from '../order-history-status'
import { OrderFilter, OrderHistory } from '../share-type'
import { Button, Card, DatePicker, Form, Input, InputNumber, Select, Space, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

const defaultPageInfo: PageInfo = { currentPage: 1, pageSize: 10, count: 0 }
const { guavapassUrl } = config
const { RangePicker } = DatePicker

type OrderHistoryFilterForm = Omit<OrderFilter, 'createdAtFrom' | 'createdAtTo'> & {
  purchasedDateRange?: [Dayjs, Dayjs]
}

const OrderHistories = () => {
  const [form] = Form.useForm<OrderHistoryFilterForm>()
  const [orders, setOrders] = useState<OrderHistory[]>([])
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPageInfo)
  const [filter, setFilter] = useState<Partial<OrderFilter>>({})

  const loadOrders = (
    params: Partial<OrderFilter> = filter,
    page = 1,
    size = pageInfo.pageSize,
  ) => {
    getOrderHistories({ ...params, page: page - 1, size }).then(res => {
      setOrders(res.data)
      setPageInfo({ ...res.pageInfo, currentPage: res.pageInfo.currentPage + 1 })
    })
  }

  useEffect(() => loadOrders({}, 1, defaultPageInfo.pageSize), [])

  const columns: ColumnsType<OrderHistory> = [
    {
      title: translate('orderHistories.orderId'),
      dataIndex: 'id',
      width: 90,
      render: id => <LinkButton to={`/order-histories/${id}`}>{id}</LinkButton>,
    },
    {
      title: translate('orderHistories.user'),
      render: record => (
        <Button type="link" target="_blank" href={`${guavapassUrl}/admin/users/${record.userId}`}>
          {record.userName ? `User#${record.userId} ${record.userName}` : `User#${record.userId}`}
        </Button>
      ),
    },
    {
      title: translate('orderHistories.orderType'),
      dataIndex: 'orderType',
      render: value =>
        translate(
          value === 'BENEFIT_BOOKING'
            ? 'orderHistories.benefitBooking'
            : 'orderHistories.planPurchase',
        ),
    },
    {
      title: translate('orderHistories.orderSubject'),
      render: record =>
        record.planId != null ? (
          <LinkButton to={`/plans/${record.planId}`}>
            {record.planExternalId ?? record.planId}
          </LinkButton>
        ) : record.benefitBooking ? (
          record.benefitBooking.benefitPlatformName
        ) : (
          '-'
        ),
    },
    {
      title: translate('orderHistories.status'),
      dataIndex: 'status',
      render: formatOrderHistoryStatus,
    },
    {
      title: translate('orderHistories.paymentAmount'),
      render: record => (record.totalFee == null ? '-' : `¥${record.totalFee / 100}`),
    },
    { title: translate('orderHistories.couponCode'), dataIndex: 'couponCode' },
    { title: translate('orderHistories.stackedCouponCode'), dataIndex: 'stackedCouponCode' },
    { title: translate('orderHistories.referralCode'), dataIndex: 'referralCode' },
    {
      title: translate('orderHistories.purchasedDate'),
      dataIndex: 'createdAt',
      render: value => getDateTime(value),
    },
    {
      title: translate('orderHistories.actions'),
      render: record => (
        <LinkButton to={`/order-histories/${record.id}`}>{translate('view')}</LinkButton>
      ),
    },
  ]

  return (
    <Card>
      <Space orientation="vertical" size={16} style={{ width: '100%' }}>
        <Form<OrderHistoryFilterForm>
          form={form}
          layout="inline"
          onFinish={values => {
            const { purchasedDateRange, ...filterValues } = values
            const filters: Partial<OrderFilter> = {
              ...filterValues,
              createdAtFrom: purchasedDateRange?.[0].startOf('day').toISOString(),
              createdAtTo: purchasedDateRange?.[1].endOf('day').toISOString(),
            }
            setFilter(filters)
            loadOrders(filters, 1)
          }}
        >
          <Form.Item name="id" label={translate('orderHistories.orderId')}>
            <InputNumber />
          </Form.Item>
          <Form.Item name="userId" label={translate('orderHistories.userId')}>
            <InputNumber />
          </Form.Item>
          <Form.Item name="planId" label={translate('orderHistories.planId')}>
            <InputNumber />
          </Form.Item>
          <Form.Item name="orderType" label={translate('orderHistories.orderType')}>
            <Select
              allowClear
              style={{ width: 160 }}
              options={[
                { value: 'PLAN_PURCHASE', label: translate('orderHistories.planPurchase') },
                { value: 'BENEFIT_BOOKING', label: translate('orderHistories.benefitBooking') },
              ]}
            />
          </Form.Item>
          <Form.Item name="status" label={translate('orderHistories.status')}>
            <Select allowClear options={orderHistoryStatusOptions} style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="couponCode" label={translate('orderHistories.couponCode')}>
            <Input />
          </Form.Item>
          <Form.Item name="stackedCouponCode" label={translate('orderHistories.stackedCouponCode')}>
            <Input />
          </Form.Item>
          <Form.Item name="prepayId" label={translate('orderHistories.prepayId')}>
            <Input />
          </Form.Item>
          <Form.Item name="transactionId" label={translate('orderHistories.transactionId')}>
            <Input />
          </Form.Item>
          <Form.Item name="outTradeNo" label={translate('orderHistories.outTradeNo')}>
            <Input />
          </Form.Item>
          <Form.Item name="refundId" label={translate('orderHistories.refundId')}>
            <Input />
          </Form.Item>
          <Form.Item name="purchasedDateRange" label={translate('orderHistories.purchasedDate')}>
            <RangePicker />
          </Form.Item>
          <Form.Item name="hasReferralCode" label={translate('orderHistories.hasReferralCode')}>
            <Select allowClear style={{ width: 120 }}>
              <Select.Option value>{translate('yes')}</Select.Option>
              <Select.Option value={false}>{translate('no')}</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="referralCode" label={translate('orderHistories.referralCode')}>
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit">
            {translate('search')}
          </Button>
        </Form>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={orders}
          pagination={{
            current: pageInfo.currentPage,
            pageSize: pageInfo.pageSize,
            total: pageInfo.count,
            onChange: (page, size) => loadOrders(filter, page, size),
          }}
          size="small"
        />
      </Space>
    </Card>
  )
}

export default OrderHistories
